<template>
  <busy-area v-if="isAllowedToSee" :busy="loading">
    <div class="flex flex-wrap gap-x-10 gap-y-10 pb-10 pt-5">
      <div class="flex flex-col justify-stretch">
        <div class="text-body-regular-bold text-table-text-gray uppercase mb-5">
          {{ $t('pages.driver.payoutList.statisticsHeader1') }}
        </div>
        <table class="statisticsTable h-full">
          <thead>
            <tr v-if="!hideFiltered || !hideSelected">
              <th class="font-normal" rowspan="2" />
              <th colspan="3" class="text-center">
                {{ $t('pages.driver.payoutList.paymentStatistic.total2') }}
              </th>
              <th v-if="!hideFiltered" colspan="3" class="text-center">
                {{ $t('pages.driver.payoutList.paymentStatistic.filtered') }}
              </th>
              <th v-if="!hideSelected" colspan="3" class="text-center">
                {{ $t('pages.driver.payoutList.paymentStatistic.selected') }}
              </th>
            </tr>
            <tr>
              <th v-if="hideFiltered && hideSelected" />
              <th>{{ $t('pages.licenseList.statistics.reward.amount_chf') }}</th>
              <th>{{ $t('pages.licenseList.statistics.reward.amount_holiday') }}</th>
              <th>{{ $t('pages.licenseList.statistics.reward.amount_voucher') }}</th>
              <th v-if="!hideFiltered">
                {{ $t('pages.licenseList.statistics.reward.amount_chf') }}
              </th>
              <th v-if="!hideFiltered">
                {{ $t('pages.licenseList.statistics.reward.amount_holiday') }}
              </th>
              <th v-if="!hideFiltered">
                {{ $t('pages.licenseList.statistics.reward.amount_voucher') }}
              </th>
              <th v-if="!hideSelected">
                {{ $t('pages.licenseList.statistics.reward.amount_chf') }}
              </th>
              <th v-if="!hideSelected">
                {{ $t('pages.licenseList.statistics.reward.amount_holiday') }}
              </th>
              <th v-if="!hideSelected">
                {{ $t('pages.licenseList.statistics.reward.amount_voucher') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="type in ['pending', 'inPayment', 'payed', 'error', 'total'] as PayoutStatus[]" :key="type">
              <td>{{ $t('pages.driver.payoutList.paymentStatistic.'+type) }}</td>
              <td>{{ formatNumberForCurrency(statistics?.total?.[type]?.chf ?? 0) }}</td>
              <td>{{ formatNumberForCurrency(statistics?.total?.[type]?.holiday ?? 0) }}</td>
              <td>{{ formatNumberForCurrency(statistics?.total?.[type]?.voucher ?? 0) }}</td>
              <td v-if="!hideFiltered">
                {{ formatNumberForCurrency(statistics?.filtered?.[type]?.chf ?? 0) }}
              </td>
              <td v-if="!hideFiltered">
                {{ formatNumberForCurrency(statistics?.filtered?.[type]?.holiday ?? 0) }}
              </td>
              <td v-if="!hideFiltered">
                {{ formatNumberForCurrency(statistics?.filtered?.[type]?.voucher ?? 0) }}
              </td>
              <td v-if="!hideSelected">
                {{ formatNumberForCurrency(statistics.selected?.[type]?.chf) }}
              </td>
              <td v-if="!hideSelected">
                {{ formatNumberForCurrency(statistics.selected?.[type]?.holiday) }}
              </td>
              <td v-if="!hideSelected">
                {{ formatNumberForCurrency(statistics.selected?.[type]?.voucher) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </busy-area>
</template>

<script lang="ts" setup>
import BusyArea from '@utils/busy/BusyArea.vue';
import { computed } from 'vue';
import { formatNumberForCurrency } from '@/utils/helpers';
import { useStore } from '@/store';
import type { PayoutStatus, Statistics } from '@/hooks/company-payout';

interface Props {
  statistics: Statistics;
  loading?: boolean;
  hideSelected?: boolean;
  hideFiltered?: boolean;
  adminsAreAllowed?: boolean;
}

const props = defineProps<Props>();

const auth = useStore();
const isAllowedToSee = computed(() => (props.adminsAreAllowed ? auth.getters['auth/IS_ADMIN'] : auth.getters['auth/IS_FINANCE_ADMIN']));
</script>

<style lang="scss" scoped>
.statisticsTable {
  @apply border;

  tr {
    td, th {
      &:not(:last-child) {
        @apply border-r;
      }
      @apply px-5 py-2;
      text-align: right;
    }

    th {
      @apply border-b;
    }

    &:last-child {
      td:not(:first-child) {
        border-top: 3px double black;
      }
    }
  }
}
</style>
